import { Component } from '@theme/component';
import {
  fetchShippingDetails,
  handleShippingError,
  debounce,
  showLoadingState,
  showErrorState,
  showEmptyState,
  renderShippingMethods,
  PRICE_DIVISOR,
  MIN_PRICE
} from '@theme/globale-shipping-shared';

const DEBOUNCE_DELAY = 300;
const INIT_DELAY = 100;
const EVENT_VARIANT_CHANGED = 'variant:changed';
const EVENT_VARIANT_SELECTED = 'variant:selected';
const SELECTOR_VARIANT_ID = '[ref="variantId"]';
const SELECTOR_VARIANT_INPUT = '[name="id"][type="hidden"]';
const SELECTOR_VARIANT_PICKER = 'variant-picker';

/**
 * Global-e Shipping Methods Component for PDP
 * Displays shipping methods and costs on product detail page
 */
class GlobaleShippingPDPComponent extends Component {
  #debouncedFetch = debounce(this.#fetchShippingDetails.bind(this), DEBOUNCE_DELAY);
  /** @type {string|number|null} */
  #currentVariantId = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(EVENT_VARIANT_CHANGED, this.#handleVariantChange);
    document.addEventListener(EVENT_VARIANT_SELECTED, this.#handleVariantChange);

    setTimeout(() => {
      const variantId = this.#getSelectedVariantId();
      if (variantId) {
        this.#currentVariantId = variantId;
      }
      this.#fetchShippingDetails();
    }, INIT_DELAY);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(EVENT_VARIANT_CHANGED, this.#handleVariantChange);
    document.removeEventListener(EVENT_VARIANT_SELECTED, this.#handleVariantChange);
  }

  /**
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    const variantId = customEvent.detail?.variantId || customEvent.detail?.id || this.#getSelectedVariantId();
    
    if (variantId && variantId !== this.#currentVariantId) {
      this.#currentVariantId = variantId;
      this.#debouncedFetch();
    }
  };

  async #fetchShippingDetails() {
    // Ensure variant ID is set
    if (!this.#currentVariantId) {
      this.#currentVariantId = this.#getSelectedVariantId();
    }

    showLoadingState(this);

    try {
      const shippingData = await fetchShippingDetails({
        element: this,
        getData: () => this.#getProductData()
      });

      if (!shippingData?.shippingRates?.length) {
        showEmptyState(this);
        return;
      }

      renderShippingMethods(this, shippingData, this.dataset.currency);
    } catch (error) {
      handleShippingError(error, {
        showError: (/** @type {string} */ msg) => showErrorState(this, msg),
        showEmptyState: () => showEmptyState(this)
      });
    }
  }

  #getProductData() {
    const { productPrice, variantId: dataVariantId, currency: dataCurrency } = this.dataset;

    // Use data attributes if available (faster path)
    if (productPrice && dataVariantId) {
      const win = /** @type {Window & {Shopify?: {currency?: {active?: string}}}} */ (window);
      return {
        price: Math.max(parseFloat(productPrice) / PRICE_DIVISOR, MIN_PRICE),
        currency: dataCurrency || win.Shopify?.currency?.active || '',
        productCode: this.#getProductCodeFromVariant(dataVariantId)
      };
    }

    // Fallback to variant data
    const variantId = this.#getSelectedVariantId();
    if (!variantId) return null;

    const variant = this.#getVariantData(variantId);
    if (!variant) return null;

    const win = /** @type {Window & {Shopify?: {currency?: {active?: string}}}} */ (window);
    const priceInCents = variant.price || variant.priceV2?.amount || 0;

    return {
      price: Math.max(typeof priceInCents === 'number' ? priceInCents / PRICE_DIVISOR : 0, MIN_PRICE),
      currency: win.Shopify?.currency?.active || '',
      productCode: variant.sku || variant.id?.toString() || ''
    };
  }

  /**
   * @param {string|number} variantId
   * @returns {string}
   */
  #getProductCodeFromVariant(variantId) {
    const variant = this.#getVariantData(variantId);
    return variant?.sku || variantId?.toString() || '';
  }

  #getSelectedVariantId() {
    const variantInput = this.querySelector(SELECTOR_VARIANT_ID) ||
                        document.querySelector(SELECTOR_VARIANT_INPUT);
    const inputEl = variantInput instanceof HTMLInputElement ? variantInput : null;
    return inputEl?.value || this.dataset.variantId || null;
  }

  /**
   * @param {string|number} variantId
   * @returns {any}
   */
  #getVariantData(variantId) {
    const win = /** @type {Window & {product?: {variants?: any[]}}} */ (window);
    if (win.product?.variants) {
      return win.product.variants.find((/** @type {any} */ v) =>
        v.id?.toString() === variantId.toString()
      );
    }

    const variantPicker = this.closest(SELECTOR_VARIANT_PICKER) || document.querySelector(SELECTOR_VARIANT_PICKER);
    if (variantPicker) {
      const selectedOption = variantPicker.querySelector(`[data-variant-id="${variantId}"]`);
      if (selectedOption instanceof HTMLElement) {
        return {
          id: variantId,
          sku: selectedOption.dataset.sku || '',
          price: parseFloat(selectedOption.dataset.price || '0')
        };
      }
    }

    return null;
  }

}

if (!customElements.get('globale-shipping-pdp')) {
  customElements.define('globale-shipping-pdp', GlobaleShippingPDPComponent);
}
